import { useState, useCallback, useMemo } from 'react';
import {
  getLocationId,
  getParentId,
  getLocationName,
  getLocationTypeId,
  getLocationTypeName,
  findLocationById,
  getChildrenOf,
  getSiblings,
  computeBreadcrumbPath
} from '../utils/locationTransformers';

/**
 * Hook for breadcrumb navigation and drill-down
 * Powers the BreadcrumbDrillView and the LocationBreadcrumbBar
 */
export const useLocationBreadcrumb = ({ flatLocations, hierarchicalLocations }) => {
  // Stack of drilled-into location IDs (for drill-down view)
  const [drillStack, setDrillStack] = useState([]);

  // Current drill level ID (last item in stack, or null for root)
  const currentDrillId = drillStack.length > 0 ? drillStack[drillStack.length - 1] : null;

  // Children at current drill level
  const childrenAtLevel = useMemo(() => {
    if (!hierarchicalLocations) return [];
    return getChildrenOf(hierarchicalLocations, currentDrillId);
  }, [hierarchicalLocations, currentDrillId]);

  // Breadcrumb path for the current drill position
  const drillBreadcrumbPath = useMemo(() => {
    if (!currentDrillId || !flatLocations.length) return [];
    return computeBreadcrumbPath(flatLocations, currentDrillId);
  }, [flatLocations, currentDrillId]);

  // Breadcrumb path for a specific location
  const getBreadcrumbPath = useCallback((locationId) => {
    return computeBreadcrumbPath(flatLocations, locationId);
  }, [flatLocations]);

  // Get siblings for a location
  const getLocationSiblings = useCallback((location) => {
    return getSiblings(flatLocations, location);
  }, [flatLocations]);

  // Navigate into a location (push to drill stack)
  const drillInto = useCallback((locationId) => {
    setDrillStack(prev => [...prev, locationId]);
  }, []);

  // Go up one level
  const drillUp = useCallback(() => {
    setDrillStack(prev => prev.slice(0, -1));
  }, []);

  // Jump to a specific level in the drill stack
  const drillToLevel = useCallback((index) => {
    if (index < 0) {
      setDrillStack([]);
    } else {
      setDrillStack(prev => prev.slice(0, index + 1));
    }
  }, []);

  // Reset drill stack (go to root)
  const resetDrill = useCallback(() => {
    setDrillStack([]);
  }, []);

  // Navigate to a specific location in drill view
  const drillToLocation = useCallback((locationId) => {
    const path = computeBreadcrumbPath(flatLocations, locationId);
    if (path.length > 0) {
      // Set drill stack to all ancestors (exclude the target itself)
      setDrillStack(path.map(p => p.id));
    }
  }, [flatLocations]);

  // Get sibling navigation info for prev/next
  const getSiblingNavigation = useCallback((location) => {
    if (!location) return { prev: null, next: null, index: -1, total: 0 };

    const siblings = getSiblings(flatLocations, location);
    const currentId = getLocationId(location);
    const currentIndex = siblings.findIndex(s => String(getLocationId(s)) === String(currentId));

    return {
      prev: currentIndex > 0 ? siblings[currentIndex - 1] : null,
      next: currentIndex < siblings.length - 1 ? siblings[currentIndex + 1] : null,
      index: currentIndex,
      total: siblings.length
    };
  }, [flatLocations]);

  return {
    // Drill state
    drillStack,
    currentDrillId,
    childrenAtLevel,
    drillBreadcrumbPath,

    // Navigation
    drillInto,
    drillUp,
    drillToLevel,
    drillToLocation,
    resetDrill,

    // Utilities
    getBreadcrumbPath,
    getLocationSiblings,
    getSiblingNavigation
  };
};

export default useLocationBreadcrumb;
